/**
 * PlatformTenantsView — `/platform/clients` (OT-0073 §4.3).
 *
 * Lista de clientes para el platform admin: estado, fin de beta, saldo,
 * usuarios, estado del administrador y última actividad; filtro por estado y
 * alta de un cliente nuevo. Cada fila abre el detalle. Sólo metadatos: nunca
 * contenido del cliente (OT-0073 §4.1).
 */

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { PlatformTenantListRow } from '../../api/platform'
import { CreateTenantDialog } from '../../components/platform/CreateTenantDialog'
import { ClientStateBadge, TrialEnd } from '../../components/platform/clientDisplay'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { formatDateTime, formatUsd } from '../../components/usage/usageHelpers'
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage'
import { usePlatformTenants } from '../../hooks/usePlatform'
import {
  ORGANIZATION_STATES,
  organizationState,
  type OrganizationState,
} from '../../lib/organizationState'

type Filter = OrganizationState | 'all'

export function PlatformTenantsView() {
  const { t, i18n } = useTranslation(['platform', 'common'])
  const errorMessage = useApiErrorMessage()
  const tenants = usePlatformTenants()
  const [filter, setFilter] = useState<Filter>('all')
  const [creating, setCreating] = useState(false)

  const rows = useMemo(() => {
    const all = [...(tenants.data ?? [])].sort((a, b) =>
      a.display_name.localeCompare(b.display_name, i18n.language),
    )
    return filter === 'all' ? all : all.filter((r) => organizationState(r) === filter)
  }, [tenants.data, filter, i18n.language])

  return (
    <ScrollPage>
      <div className="max-w-7xl">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold text-ink">
              {t('clients.title')}
            </h1>
            <p className="mt-1 max-w-prose font-body text-sm text-ink-muted">
              {t('clients.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-canvas hover:bg-brand/90"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('clients.new_client')}
          </button>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2" role="group">
          {(['all', ...ORGANIZATION_STATES] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={f === filter}
              onClick={() => setFilter(f)}
              className={
                f === filter
                  ? 'rounded-md border border-brand bg-brand px-3 py-1 text-xs font-medium text-canvas'
                  : 'rounded-md border border-line bg-surface px-3 py-1 text-xs text-ink-muted hover:bg-surface-muted'
              }
            >
              {f === 'all' ? t('clients.filter_all') : t(`clients.state.${f}`)}
            </button>
          ))}
        </div>

        {tenants.isPending ? (
          <p className="py-4 text-sm text-ink-muted">{t('common:actions.loading')}</p>
        ) : tenants.error ? (
          <p role="alert" className="py-4 text-sm text-critical">
            {errorMessage(tenants.error)}
          </p>
        ) : rows.length === 0 ? (
          <p className="rounded-md border border-line bg-surface px-4 py-6 text-sm text-ink-muted">
            {filter === 'all' ? t('clients.empty') : t('clients.empty_filtered')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-subtle">
                <tr>
                  <Th>{t('clients.col.client')}</Th>
                  <Th>{t('clients.col.state')}</Th>
                  <Th>{t('clients.col.trial_end')}</Th>
                  <Th className="text-right">{t('clients.col.balance')}</Th>
                  <Th className="text-right">{t('clients.col.members')}</Th>
                  <Th>{t('clients.col.admin')}</Th>
                  <Th>{t('clients.col.last_activity')}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <ClientRow key={row.tenant_id} row={row} locale={i18n.language} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateTenantDialog open={creating} onClose={() => setCreating(false)} />
    </ScrollPage>
  )
}

function ClientRow({ row, locale }: { row: PlatformTenantListRow; locale: string }) {
  const { t } = useTranslation('platform')
  return (
    <tr className="border-t border-line hover:bg-surface-muted">
      <td className="px-3 py-2 align-middle">
        <Link
          to="/platform/clients/$tenantId"
          params={{ tenantId: row.tenant_id }}
          className="font-medium text-ink hover:text-brand"
        >
          {row.display_name}
        </Link>
        <div className="font-mono text-[11px] text-ink-subtle">{row.slug}</div>
      </td>
      <td className="px-3 py-2 align-middle">
        <ClientStateBadge client={row} />
      </td>
      <td className="px-3 py-2 align-middle">
        <TrialEnd trialEndsAt={row.trial_ends_at} locale={locale} />
      </td>
      <td className="px-3 py-2 text-right align-middle font-mono">
        {formatUsd(row.balance_usd, locale)}
      </td>
      <td className="px-3 py-2 text-right align-middle font-mono">{row.members}</td>
      <td className="px-3 py-2 align-middle text-ink-muted">
        {t(`clients.admin_state.${row.admin_state}`)}
      </td>
      <td className="px-3 py-2 align-middle text-ink-muted">
        {formatDateTime(row.last_activity_at, locale)}
      </td>
    </tr>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-3 py-2 text-left font-medium ${className ?? ''}`}>{children}</th>
}
